import type { ComponentType } from "react";

import { compact } from "lodash";

import type { Meter } from "@eisox/api/core-api";
import { ArrowDownIcon, BarChartIcon, SwapIcon, TargetIcon, type IconProps } from "@eisox/icons";
import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";

import { KpiGrid, type Kpi } from "~/components/kpi-grid";
import { EMPTY_VALUE } from "~/constants/display.constant";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import { COMPARISONS, GRANULARITIES } from "../consumption.constant";
import { chartQueries } from "../consumption.queries";
import { appliedOptions, formatComparedBucket } from "../helpers/comparison.helper";
import type { ConsumptionSearch } from "../helpers/consumption-search.helper";
import { energyIcon, formatConsumption, formatPercent, unitOf } from "../helpers/energy.helper";
import { formatBucket } from "../helpers/period.helper";
import { chartSeries, consumptionRows, consumptionTotal, difference, hasConsumption } from "../helpers/series.helper";
import { SeriesKey, type ChartView } from "../types/consumption.type";
import { ComparisonDrawer } from "./comparison-drawer";
import { ConsumptionChart } from "./consumption-chart";
import { ConsumptionTooltip } from "./consumption-tooltip";
import { ExportMenu } from "./export-menu";

const NAME = "MeterConsumption";

interface MeterConsumptionProps {
  meter: Meter;
  view: ChartView;
  today: string;
  onSearchChange: (search: Partial<ConsumptionSearch>) => void;
  onBucketOpen?: (start: string, compared: boolean) => void;
}

const MeterConsumption: React.FC<MeterConsumptionProps> = ({ meter, view, today, onSearchChange, onBucketOpen }) => {
  const { building } = useBuildingContext(NAME);
  const queries = chartQueries(building, meter.id, view);
  const {
    data: { buckets, average, baseload },
  } = useSuspenseQuery(queries.current);
  const [compared] = useSuspenseQueries({ queries: compact([queries.compared]) });

  const { period, comparison, degreeDays } = view;
  const unit = unitOf(meter);
  const rows = consumptionRows(
    buckets,
    { granularity: period.granularity, timezone: building.timezone },
    comparison && compared && { applied: comparison, buckets: compared.data.buckets },
  );
  const comparisonLabel = comparison && COMPARISONS[comparison.comparison].name();
  const objectiveLabel =
    comparison?.objective === undefined
      ? undefined
      : m.consumption_target_series({ target: formatPercent(-comparison.objective / 100, { signed: true }) });
  const { bucketFormat, tickFormat } = GRANULARITIES[period.granularity];
  const formatStart = (format: string) => (start: string) => formatBucket(start, format, building.timezone);
  const formatComparedStart = (format: string) =>
    comparison && ((start: string) => formatComparedBucket(comparison, start, format, building.timezone));

  if (!hasConsumption(buckets)) {
    return <p className="text-primary-dark-gray">{m.no_result()}</p>;
  }

  const differenceKpi = (Icon: ComponentType<IconProps>, target: string, base: SeriesKey, baseDju: SeriesKey): Kpi => {
    const gap = degreeDays
      ? difference(rows, SeriesKey.CONSUMPTION_DJU, baseDju)
      : difference(rows, SeriesKey.CONSUMPTION, base);
    const name = degreeDays ? m.consumption_series_dju({ name: target }) : target;
    const help = m.consumption_difference_help();
    if (!gap) return { Icon, value: EMPTY_VALUE, label: m.consumption_versus({ target: name }), help };
    return {
      Icon,
      value: formatPercent(gap.ratio, { signed: true }),
      label: m.consumption_difference_versus({
        difference: formatConsumption(gap.amount, unit, { signed: true }),
        target: name,
      }),
      help,
      color: gap.ratio > 0 ? "red" : "green",
    };
  };

  const kpis: Kpi[] = [
    { Icon: energyIcon(meter), value: formatConsumption(consumptionTotal(buckets), unit), label: m.consumption() },
    ...(comparisonLabel
      ? [differenceKpi(SwapIcon, comparisonLabel, SeriesKey.COMPARISON, SeriesKey.COMPARISON_DJU)]
      : []),
    ...(objectiveLabel
      ? [differenceKpi(TargetIcon, objectiveLabel, SeriesKey.OBJECTIVE, SeriesKey.OBJECTIVE_DJU)]
      : []),
    {
      Icon: BarChartIcon,
      value: average?.consumption ? formatConsumption(average.consumption.value, unit) : EMPTY_VALUE,
      label: GRANULARITIES[period.granularity].averageLabel(),
    },
    ...(baseload?.consumption
      ? [
          {
            Icon: ArrowDownIcon,
            value: formatConsumption(baseload.consumption.value, unit),
            label: m.consumption_baseload(),
            help: m.consumption_baseload_help(),
          },
        ]
      : []),
  ];

  return (
    <div className="gap-2.5 md:gap-5 min-h-0 flex flex-1 flex-col">
      <KpiGrid kpis={kpis} />
      <div className="gap-5 flex items-center justify-end">
        <ComparisonDrawer
          values={{ ...appliedOptions(view), objective: comparison?.objective }}
          meter={meter}
          period={period}
          today={today}
          onApply={onSearchChange}
        />
        <ExportMenu meter={meter} view={view} />
      </div>
      <ConsumptionChart
        rows={rows}
        series={chartSeries(rows, {
          [SeriesKey.CONSUMPTION]: m.consumption(),
          [SeriesKey.CONSUMPTION_DJU]: m.consumption_series_dju({ name: m.consumption() }),
          ...(comparisonLabel && {
            [SeriesKey.COMPARISON]: comparisonLabel,
            [SeriesKey.COMPARISON_DJU]: m.consumption_series_dju({ name: comparisonLabel }),
          }),
          ...(objectiveLabel && {
            [SeriesKey.OBJECTIVE]: objectiveLabel,
            [SeriesKey.OBJECTIVE_DJU]: m.consumption_series_dju({ name: objectiveLabel }),
          }),
        })}
        unit={unit}
        baseload={baseload?.consumption?.value}
        formatTick={start =>
          compact([formatStart(tickFormat)(start), formatComparedStart(tickFormat)?.(start)]).join(" / ")
        }
        formatValue={value => formatConsumption(value, unit)}
        formatTooltipTitle={formatStart(bucketFormat)}
        renderTooltip={start => {
          const row = rows.find(candidate => candidate.start === start);
          return (
            row && (
              <ConsumptionTooltip
                row={row}
                formatStart={formatStart(bucketFormat)}
                unit={unit}
                degreeDays={degreeDays}
                formatComparedStart={formatComparedStart(bucketFormat)}
                objectiveLabel={objectiveLabel}
              />
            )
          );
        }}
        onPointSelect={
          onBucketOpen &&
          ((start, series) =>
            onBucketOpen(start, series === SeriesKey.COMPARISON || series === SeriesKey.COMPARISON_DJU))
        }
      />
    </div>
  );
};

MeterConsumption.displayName = NAME;

export { MeterConsumption };
